import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useToast } from '@/components/toastStore';
import { useCurvaPiensoAjustes, useGuardarCurvaPienso } from '@/lib/queries/catalogos';
import { extractApiMessage } from '@/lib/api';
import { useCurveEditor } from './curveEditor';
import { useAuthStore } from '@/stores/auth';

type Sexo = 'machos' | 'hembras';

export default function AjustesPiensoPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const role = useAuthStore((state) => state.user?.rol);
  const canWrite = role === 'admin' || role === 'ganadero';
  const canRead = canWrite || role === 'veterinario';
  const editor = useCurveEditor('pienso');
  const { sexo, filas, hydrate, guardando } = editor;
  const duracionCamada = sexo === 'machos' ? 120 : 90;
  const ticksEdad = sexo === 'machos' ? [0, 30, 60, 90, 120] : [0, 30, 60, 90];
  const curvaQ = useCurvaPiensoAjustes(sexo);
  const guardar = useGuardarCurvaPienso(sexo);
  useEffect(() => {
    if (!curvaQ.data) return;
    hydrate(
      (curvaQ.data?.puntos ?? []).map((punto) => ({
        edad: String(punto.edad_dias),
        valor: String(punto.kg_acumulados).replace('.', ','),
      })),
    );
  }, [sexo, curvaQ.data, hydrate]);

  const chartData = editor.points.map((p) => ({ edad: p.edad, toneladas: p.valor / 1000 }));

  function actualizar(i: number, campo: 'edad' | 'valor', valor: string) {
    if (!canWrite) return;
    editor.changeRows(
      filas.map((fila, indice) => (indice === i ? { ...fila, [campo]: valor } : fila)),
    );
  }

  function anadir() {
    if (!canWrite) return;
    const ultima = filas.at(-1);
    const edad = ultima ? Number(ultima.edad) + 7 : 1;
    editor.changeRows([...filas, { edad: String(Number.isFinite(edad) ? edad : 1), valor: '' }]);
  }

  async function guardarCambios() {
    if (!canWrite || curvaQ.isLoading || curvaQ.isError) return;
    const parsed = editor.startSave();
    if (!parsed) return;
    const puntos = parsed.map((punto) => ({
      edad_dias: punto.edad,
      kg_acumulados: punto.valor,
    }));
    try {
      const confirmed = await guardar.mutateAsync({ puntos });
      editor.saved(
        confirmed.puntos.map((p) => ({
          edad: String(p.edad_dias),
          valor: String(p.kg_acumulados).replace('.', ','),
        })),
      );
      toast.show({ kind: 'success', message: t('pienso_ajustes.guardado') });
    } catch (err) {
      editor.failSave(err);
    } finally {
      editor.finishSave();
    }
  }

  if (!canRead)
    return (
      <main className="ui-page">
        <p role="alert">No tienes permiso para consultar esta curva.</p>
        {editor.dialog}
      </main>
    );

  if (curvaQ.isError) {
    return (
      <main className="mx-auto max-w-md px-4 pb-8 pt-2">
        <p role="alert">{extractApiMessage(curvaQ.error)}</p>
        {editor.dialog}
      </main>
    );
  }

  return (
    <main className="ui-page pb-8">
      <h1 className="ui-page-title mb-1">{t('pienso_ajustes.titulo')}</h1>
      <p className="mb-5 text-sm text-text-secondary">{t('pienso_ajustes.subtitulo')}</p>

      <div className="mb-4 grid grid-cols-2 gap-2">
        {(['machos', 'hembras'] as Sexo[]).map((opcion) => (
          <button
            key={opcion}
            type="button"
            aria-pressed={sexo === opcion}
            disabled={guardando || curvaQ.isLoading}
            onClick={() => editor.changeSexo(opcion)}
            className={`min-h-12 rounded-lg border px-3 py-2 text-sm font-medium disabled:opacity-50 ${sexo === opcion ? 'border-accent bg-accent text-accent-contrast' : 'border-border bg-surface text-text-primary'}`}
          >
            {t(`pienso_ajustes.${opcion}`)}
          </button>
        ))}
      </div>

      {editor.errorSummary}

      {curvaQ.isLoading ? (
        <p className="text-sm text-text-secondary">{t('common.loading')}</p>
      ) : (
        <>
          <div className="mb-4 rounded-xl border border-border bg-surface p-2">
            {chartData.length > 1 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
                  <XAxis
                    dataKey="edad"
                    type="number"
                    domain={[0, duracionCamada]}
                    ticks={ticksEdad}
                    tick={{ fontSize: 10, fill: 'var(--c-text-secondary)' }}
                    tickFormatter={(v) => `D${v}`}
                  />
                  <YAxis
                    width={32}
                    tick={{ fontSize: 10, fill: 'var(--c-text-secondary)' }}
                    tickFormatter={(v) => `${v} t`}
                  />
                  <Tooltip
                    formatter={(value) => [
                      `${Number(value).toFixed(2).replace('.', ',')} t`,
                      t('pienso_ajustes.toneladas_acumuladas'),
                    ]}
                    labelFormatter={(label) => t('pienso.dia_camada', { n: label })}
                    contentStyle={{
                      backgroundColor: 'var(--c-surface)',
                      border: '1px solid var(--c-border)',
                      borderRadius: '0.5rem',
                      color: 'var(--c-text-primary)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                    }}
                    labelStyle={{
                      color: 'var(--c-text-secondary)',
                      fontSize: '0.75rem',
                      marginBottom: '0.25rem',
                    }}
                    itemStyle={{
                      color: 'var(--c-text-primary)',
                      fontSize: '0.8125rem',
                      padding: '1px 0',
                    }}
                    cursor={{ stroke: 'var(--c-border-strong)', strokeWidth: 1 }}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="toneladas"
                    stroke="#378ADD"
                    strokeWidth={2}
                    dot={{ r: 2.5, fill: 'var(--c-surface)', stroke: '#378ADD', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-8 text-center text-sm text-text-secondary">
                {t('pienso_ajustes.sin_puntos')}
              </p>
            )}
          </div>

          <div className="space-y-2">
            {filas.map((fila, i) => (
              <div key={i} className="space-y-1">
                <div className="flex min-w-0 items-center gap-2">
                  <label className="sr-only" htmlFor={editor.fieldProps(i, 'edad').id}>
                    {t('pienso_ajustes.dia')} {i + 1}
                  </label>
                  <input
                    {...editor.fieldProps(i, 'edad')}
                    disabled={!canWrite || guardando}
                    value={fila.edad}
                    onChange={(e) => actualizar(i, 'edad', e.target.value)}
                    aria-label={`${t('pienso_ajustes.dia')} ${i + 1}`}
                    inputMode="numeric"
                    className="min-h-12 w-20 shrink-0 rounded-lg border border-border bg-surface px-3 py-2 text-center"
                  />
                  <span className="text-sm text-text-secondary">{t('pienso_ajustes.dia')}</span>
                  <label className="sr-only" htmlFor={editor.fieldProps(i, 'valor').id}>
                    {t('pienso_ajustes.kg_acumulados')} {i + 1}
                  </label>
                  <input
                    {...editor.fieldProps(i, 'valor')}
                    disabled={!canWrite || guardando}
                    value={fila.valor}
                    onChange={(e) => actualizar(i, 'valor', e.target.value)}
                    aria-label={`${t('pienso_ajustes.kg_acumulados')} ${i + 1}`}
                    inputMode="decimal"
                    className="min-h-12 min-w-0 flex-1 rounded-lg border border-border bg-surface px-3 py-2 text-right"
                  />
                  <span className="text-sm text-text-secondary">kg</span>
                  <button
                    type="button"
                    onClick={() => {
                      if (!canWrite) return;
                      editor.changeRows(filas.filter((_fila, indice) => indice !== i));
                    }}
                    aria-label={t('pienso_ajustes.eliminar')}
                    disabled={!canWrite || guardando}
                    className="flex size-12 shrink-0 items-center justify-center rounded-lg border border-border text-danger disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </button>
                </div>
                {(['edad', 'valor'] as const).map(
                  (field) =>
                    editor.errores[i]?.[field] && (
                      <p
                        key={field}
                        id={`${editor.fieldProps(i, field).id}-error`}
                        className="text-sm text-warning-text"
                      >
                        {editor.errores[i][field]}
                      </p>
                    ),
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={anadir}
              disabled={!canWrite || guardando}
              className="inline-flex min-h-11 flex-1 items-center justify-center gap-1 rounded-lg border border-border bg-surface px-3 py-2 text-sm font-medium text-text-primary"
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t('pienso_ajustes.anadir')}
            </button>
            <button
              type="button"
              onClick={guardarCambios}
              disabled={!canWrite || guardando}
              className="min-h-11 flex-1 rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-accent-contrast disabled:opacity-60"
            >
              {guardando ? 'Guardando…' : t('pienso_ajustes.guardar')}
            </button>
          </div>
        </>
      )}
      {editor.dialog}
    </main>
  );
}
